import { useEffect, useState } from 'react';

/**
 * useSavedViews — gestisce un elenco di "viste salvate" (preset di filtri)
 * dentro localStorage, isolate per `scopeKey` (es. 'overview' / 'area').
 *
 * Ogni vista ha la forma: { id, name, payload, created_at }
 * `payload` è un oggetto opaco scelto dalla pagina chiamante.
 */
const lsKey = (scopeKey) => `storeops:saved-views:${scopeKey}`;
const EVENT = 'storeops:saved-views-changed';

function readAll(scopeKey) {
  try {
    const raw = localStorage.getItem(lsKey(scopeKey));
    const arr = raw ? JSON.parse(raw) : [];
    return Array.isArray(arr) ? arr : [];
  } catch {
    return [];
  }
}

function writeAll(scopeKey, arr) {
  try {
    localStorage.setItem(lsKey(scopeKey), JSON.stringify(arr));
    window.dispatchEvent(new CustomEvent(EVENT, { detail: { scopeKey } }));
  } catch { /* storage might be blocked; silent no-op */ }
}

export function useSavedViews(scopeKey) {
  const [views, setViews] = useState(() => readAll(scopeKey));

  // Resta in sync se altre componenti dello stesso scope cambiano la lista.
  useEffect(() => {
    const onChange = (e) => {
      if (e?.detail?.scopeKey === scopeKey) setViews(readAll(scopeKey));
    };
    const onStorage = () => setViews(readAll(scopeKey));
    window.addEventListener(EVENT, onChange);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener(EVENT, onChange);
      window.removeEventListener('storage', onStorage);
    };
  }, [scopeKey]);

  const save = (name, payload) => {
    const trimmed = (name || '').trim();
    if (!trimmed) return null;
    const newView = {
      id: `v_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 6)}`,
      name: trimmed,
      payload,
      created_at: new Date().toISOString(),
    };
    const next = [newView, ...readAll(scopeKey)].slice(0, 20); // safety cap
    writeAll(scopeKey, next);
    setViews(next);
    return newView;
  };

  const remove = (id) => {
    const next = readAll(scopeKey).filter((v) => v.id !== id);
    writeAll(scopeKey, next);
    setViews(next);
  };

  return { views, save, remove };
}
